import { lazy, Suspense, useEffect } from "react";
import BackToTop from "@/components/BackToTop";
import NewHero from "@/components/NewHero";
import NewProjectsSection from "@/components/NewProjectsSection";
import PillNavbar from "@/components/PillNavbar";
import ScrollProgress from "@/components/ScrollProgress";
import Section from "@/components/Section";
import { Skeleton } from "@/components/ui/skeleton";

const LazyExperienceSection = lazy(() => import("@/components/ExperienceSection"));
const LazyEducationSection = lazy(() => import("@/components/EducationSection"));
const LazySkillsSection = lazy(() => import("@/components/SkillsSection"));
const LazyContactSection = lazy(() => import("@/components/ContactSection"));

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

/**
 * Sections mount lazily, so on a deep link (/#projects) the browser has already
 * given up looking for the anchor by the time it exists. Re-run the scroll once
 * React has painted.
 */
const useHashScroll = () => {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    let frame = 0;
    const timer = window.setTimeout(() => {
      frame = requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });
      });
    }, 120);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, []);
};

const Index = () => {
  useHashScroll();

  return (
  <div className="relative min-h-screen text-foreground">
    {/* Single static ambient layer */}
    <div className="ambient" aria-hidden="true" />

    <ScrollProgress />
    <PillNavbar />
    <BackToTop />

    <main className="relative z-10 pt-24 md:pt-28">
      <div id="home">
        <NewHero />
      </div>

      <Section
        id="experience"
        index="01"
        label="Experience"
        title={
          <>
            Eight roles, one
            <br className="hidden sm:block" /> throughline: shipping.
          </>
        }
      >
        <Suspense fallback={<LoadingSkeleton />}>
          <LazyExperienceSection />
        </Suspense>
      </Section>

      <Section id="education" index="02" label="Education" title="Where the fundamentals came from.">
        <Suspense fallback={<LoadingSkeleton />}>
          <LazyEducationSection />
        </Suspense>
      </Section>

      <Section
        id="projects"
        index="03"
        label="Selected work"
        title="Systems, with numbers attached."
        lead="Each of these ran against real data and was measured. The number is the headline; the stack is underneath."
      >
        <NewProjectsSection />
      </Section>

      <Section id="skills" index="04" label="Toolkit" title="What I reach for.">
        <Suspense fallback={<LoadingSkeleton />}>
          <LazySkillsSection />
        </Suspense>
      </Section>

      <Section id="contact" index="05" label="Contact" title="Let's talk.">
        <Suspense fallback={<LoadingSkeleton />}>
          <LazyContactSection />
        </Suspense>
      </Section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-3 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-10">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Jeet Patel
          </p>
          <p className="type-label text-muted-foreground/50">
            React · TypeScript · Tailwind
          </p>
        </div>
      </footer>
    </main>
  </div>
  );
};

export default Index;
